import React, { useEffect, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GetData from "../../../services/getData";
import logPatientChange from "../../../services/patientLogs";
import { buildPatientLogText } from "../../../services/patientLogMessages";
import BlockTitle from "../../../components/layout/blockTitle/BlockTitle";
import { Button } from "../../../components/ui";
import DateInput from "../../../components/ui/DateInput";
import AiVoiceDictationModal, {
  setFormValueByPath,
} from "../../../components/ui/AiVoiceDictationModal";
import "../patients.css";
import "./PatientPrimaryVisit.css";

// value — доменное значение для сервера (не переводится)
const VISIT_PLACES = [
  { value: "на приеме", labelKey: "ppv.places.atAppointment" },
  { value: "на дому", labelKey: "ppv.places.atHome" },
  {
    value: "в фельдшерско-акушерском пункте",
    labelKey: "ppv.places.feldsher",
  },
  { value: "прочее", labelKey: "ppv.places.other" },
];

const getVisitPlaceLabel = (value, t) => {
  const option = VISIT_PLACES.find((p) => p.value === value);
  return option ? t(option.labelKey) : value || "";
};

const todayIso = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const formatDateForDisplay = (isoDate) => {
  if (!isoDate) return "";
  const [datePart] = String(isoDate).trim().split(/[ T]/);
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return String(isoDate);
  return `${day}.${month}.${year}`;
};

const emptyForm = () => ({
  exam_date: todayIso(),
  visit_place: "на приеме",
  doctor: "",
  complaints: "",
  anamnesis: "",
  objective_data: "",
  main_diagnosis: "",
  main_diagnosis_mkb: "",
  complications: "",
  complications_mkb: "",
  concomitant_1: "",
  concomitant_1_mkb: "",
  concomitant_2: "",
  concomitant_2_mkb: "",
  external_cause: "",
  external_cause_mkb: "",
  health_group: "",
  dispensary_observation: "",
  prescriptions: "",
  medications: "",
  sick_leave: "",
  preferential_prescriptions: "",
});

const parseDocData = (raw) => {
  if (!raw) return {};
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return {};
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch (_) {
      return {};
    }
  }
  return {};
};

const mapDocDataToFormData = (raw, defaults = {}) => {
  const data = parseDocData(raw);
  const base = emptyForm();
  return {
    ...base,
    ...defaults,
    exam_date: data.exam_date || defaults.exam_date || base.exam_date,
    visit_place: data.visit_place || defaults.visit_place || base.visit_place,
    doctor:
      data.doctor != null && String(data.doctor).trim() !== ""
        ? String(data.doctor)
        : defaults.doctor || base.doctor,
    complaints: data.complaints ?? "",
    anamnesis: data.anamnesis ?? "",
    objective_data: data.objective_data ?? "",
    main_diagnosis: data.main_diagnosis ?? "",
    main_diagnosis_mkb: data.main_diagnosis_mkb ?? "",
    complications: data.complications ?? "",
    complications_mkb: data.complications_mkb ?? "",
    concomitant_1: data.concomitant_1 ?? "",
    concomitant_1_mkb: data.concomitant_1_mkb ?? "",
    concomitant_2: data.concomitant_2 ?? "",
    concomitant_2_mkb: data.concomitant_2_mkb ?? "",
    external_cause: data.external_cause ?? "",
    external_cause_mkb: data.external_cause_mkb ?? "",
    health_group: data.health_group ?? "",
    dispensary_observation: data.dispensary_observation ?? "",
    prescriptions: data.prescriptions ?? "",
    medications: data.medications ?? "",
    sick_leave: data.sick_leave ?? "",
    preferential_prescriptions: data.preferential_prescriptions ?? "",
  };
};

const buildDocDataPayload = (form) => ({
  exam_date: form.exam_date ?? "",
  visit_place: form.visit_place ?? "",
  doctor: form.doctor ?? "",
  complaints: form.complaints ?? "",
  anamnesis: form.anamnesis ?? "",
  objective_data: form.objective_data ?? "",
  main_diagnosis: form.main_diagnosis ?? "",
  main_diagnosis_mkb: form.main_diagnosis_mkb ?? "",
  complications: form.complications ?? "",
  complications_mkb: form.complications_mkb ?? "",
  concomitant_1: form.concomitant_1 ?? "",
  concomitant_1_mkb: form.concomitant_1_mkb ?? "",
  concomitant_2: form.concomitant_2 ?? "",
  concomitant_2_mkb: form.concomitant_2_mkb ?? "",
  external_cause: form.external_cause ?? "",
  external_cause_mkb: form.external_cause_mkb ?? "",
  health_group: form.health_group ?? "",
  dispensary_observation: form.dispensary_observation ?? "",
  prescriptions: form.prescriptions ?? "",
  medications: form.medications ?? "",
  sick_leave: form.sick_leave ?? "",
  preferential_prescriptions: form.preferential_prescriptions ?? "",
});

const formatDoctorLabel = (doctor) => {
  if (!doctor) return "";
  if (typeof doctor === "string") return doctor.trim();
  const name = String(doctor.name ?? doctor?.name?.value ?? "").trim();
  const post = String(doctor.post ?? doctor?.post?.value ?? "").trim();
  if (name && post) return `${name}, ${post}`;
  return name || post || "";
};

const normalizeDoctorsList = (raw) => {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const list = [];
  raw.forEach((item) => {
    const label = formatDoctorLabel(item);
    if (!label || seen.has(label)) return;
    seen.add(label);
    list.push({
      id: item?.id ?? item?.id?.value ?? label,
      label,
    });
  });
  return list;
};

const fetchCurrentDoctorLabel = async () => {
  try {
    const auth = await GetData({ module: "auth", block: "check" });
    const user = auth?.user;
    if (!user?.id) return user?.name || "";

    let post = "";
    try {
      const staffRes = await GetData({
        module: "staff",
        block: "info",
        id_staff: user.id,
      });
      let employee = staffRes?.result || {};
      if (Array.isArray(employee) && employee.length > 0) {
        employee = employee[0];
      }
      post = employee?.post?.value || employee?.type?.value || "";
    } catch (_) {
      /* должность необязательна */
    }

    const name = user.name || "";
    if (name && post) return `${name}, ${post}`;
    return name || post || "";
  } catch (_) {
    return "";
  }
};

function PatientPrimaryVisit() {
  const { t } = useTranslation("patient");
  const navigate = useNavigate();
  const { patientId, docId } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(emptyForm());
  const [savedFormData, setSavedFormData] = useState(emptyForm());
  const [doctors, setDoctors] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [voiceUndoSnapshot, setVoiceUndoSnapshot] = useState(null);
  const shouldAutoPrint = searchParams.get("print") === "1";

  useEffect(() => {
    loadDocument();
  }, [patientId, docId]);

  const showAlert = (message) => {
    setModalMessage(message);
    setShowModal(true);
  };

  const loadDocument = async () => {
    setLoading(true);
    setError(null);
    try {
      const [docRes, doctorLabel, patientInfo] = await Promise.all([
        GetData({
          module: "patient",
          block: "patient_docs",
          action: "get",
          id_patient: patientId,
          id: docId,
        }),
        fetchCurrentDoctorLabel(),
        GetData({
          module: "patient",
          block: "info",
          id_patient: patientId,
        }),
      ]);

      if (docRes?.status === "fail") {
        throw new Error(docRes?.message || t("docs.errors.notFound"));
      }

      const doc = docRes?.result ?? null;
      if (!doc?.id) {
        throw new Error(t("docs.errors.notFound"));
      }
      if (Number(doc.template_id) !== 1) {
        throw new Error(t("ppv.notPrimaryVisit"));
      }

      const doctorsList = normalizeDoctorsList(
        patientInfo?.result?.all_doctors || []
      );
      setDoctors(doctorsList);

      const defaults = {
        exam_date: todayIso(),
        doctor: doctorLabel,
        visit_place: "на приеме",
      };
      const mapped = mapDocDataToFormData(doc.data, defaults);
      setFormData(mapped);
      setSavedFormData(mapped);
    } catch (err) {
      setError(err.message || t("docs.errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = () => setIsEditing(true);

  // ИИ-диктовка: перенос данных из диктовки врача в поля формы
  const handleVoiceOpen = () => setShowVoiceModal(true);

  const handleVoiceApply = (items) => {
    if (!Array.isArray(items) || items.length === 0) return;
    setVoiceUndoSnapshot(formData);
    setFormData((prev) =>
      items.reduce(
        (acc, item) => setFormValueByPath(acc, item.path, item.value),
        prev,
      ),
    );
  };

  const handleVoiceUndo = () => {
    if (voiceUndoSnapshot) {
      setFormData(voiceUndoSnapshot);
    }
    setVoiceUndoSnapshot(null);
  };

  const handleCancel = () => {
    setFormData(savedFormData);
    setIsEditing(false);
  };

  const handleSave = async () => {
    const payload = buildDocDataPayload(formData);
    try {
      const result = await GetData({
        module: "patient",
        block: "patient_docs",
        action: "update",
        id_patient: patientId,
        id: docId,
        data: payload,
      });

      if (result?.status === "fail") {
        throw new Error(result?.message || t("docs.errors.saveFailed"));
      }

      const mapped = mapDocDataToFormData(result?.result?.data ?? payload);
      setFormData(mapped);
      setSavedFormData(mapped);
      setIsEditing(false);
      showAlert(t("docs.errors.saved"));
    } catch (err) {
      console.error("Ошибка сохранения:", err);
      showAlert(t("docs.errors.saveFailedWith", { message: err.message }));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t("ppv.confirmDelete"))) return;

    try {
      const result = await GetData({
        module: "patient",
        block: "patient_docs",
        action: "delete",
        id_patient: patientId,
        id: docId,
      });

      if (result?.status !== "success") {
        throw new Error(result?.message || t("docs.errors.deleteFailed"));
      }

      await logPatientChange({
        idPatient: patientId,
        idItem: docId,
        text: buildPatientLogText("patient_docs_deleted", {
          details: "Первичный прием",
        }),
      });

      navigate(`/patient/${patientId}/profile`);
    } catch (err) {
      console.error("Ошибка удаления:", err);
      showAlert(t("docs.errors.deleteFailedWith", { message: err.message }));
    }
  };

  const handlePrint = () => {
    if (typeof window === "undefined") return;
    const body = document.body;
    if (!body) {
      window.print();
      return;
    }

    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      body.classList.remove("print-only-profile", "print-profile");
      window.removeEventListener("afterprint", cleanup);
    };

    window.addEventListener("afterprint", cleanup);
    body.classList.add("print-only-profile", "print-profile");
    window.print();
  };

  useEffect(() => {
    if (!shouldAutoPrint || loading || error) return;
    const timer = window.setTimeout(() => {
      handlePrint();
      navigate(`/patient/${patientId}/profile/primary/${docId}`, {
        replace: true,
      });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [shouldAutoPrint, loading, error, patientId, docId, navigate]);

  if (loading) return <div className="loading">{t("state.loading", { ns: "common" })}</div>;
  if (error) return <div className="error">{t("error", { ns: "common", message: error })}</div>;

  const f = isEditing ? formData : savedFormData;
  const readOnly = !isEditing;
  const doctorOptions = (() => {
    const list = [...doctors];
    const current = String(f.doctor || "").trim();
    if (current && !list.some((d) => d.label === current)) {
      list.unshift({ id: `custom:${current}`, label: current });
    }
    return list;
  })();

  const renderDoctorSelect = (className) => (
    <Form.Select
      className={className}
      name="doctor"
      value={f.doctor || ""}
      onChange={handleChange}>
      <option value="">{t("docs.doctorPlaceholder")}</option>
      {doctorOptions.map((d) => (
        <option key={d.id} value={d.label}>
          {d.label}
        </option>
      ))}
    </Form.Select>
  );

  return (
    <>
      <div className="patient-name-title-sticky">
        <BlockTitle
          title={t("ppv.title")}
          buttons={
            isEditing
              ? [
                  {
                    label: t("docs.buttons.dictate"),
                    onClick: handleVoiceOpen,
                    type: "light icon",
                  },
                  {
                    label: t("docs.buttons.save"),
                    onClick: handleSave,
                    type: "normal",
                  },
                  {
                    label: t("docs.buttons.cancel"),
                    onClick: handleCancel,
                    type: "light",
                  },
                ]
              : [
                  {
                    label: t("docs.buttons.edit"),
                    onClick: handleEdit,
                    type: "light icon",
                  },
                  {
                    label: t("docs.buttons.print"),
                    onClick: handlePrint,
                    type: "light icon",
                  },
                  {
                    label: t("docs.buttons.delete"),
                    onClick: handleDelete,
                    type: "danger",
                  },
                  {
                    label: t("docs.buttons.backToList"),
                    onClick: () => navigate(`/patient/${patientId}/profile`),
                    type: "normal icon",
                  },
                ]
          }
        />
      </div>

      <Form className={`ppv-form${isEditing ? " ppv-form--editing" : ""}`}>
        <div className="ppv-section">
          <div className="ppv-section-title">{t("ppv.sectionExam")}</div>
          <div className="ppv-row">
            <div className="ppv-field">
              <Form.Label>{t("ppv.fields.examDate")}</Form.Label>
              {readOnly ? (
                <div className="form-control-plaintext">
                  {formatDateForDisplay(f.exam_date) || "—"}
                </div>
              ) : (
                <DateInput
                  className="form-control"
                  name="exam_date"
                  value={f.exam_date || ""}
                  onChange={handleChange}
                />
              )}
            </div>
            <div className="ppv-field">
              <Form.Label>{t("ppv.fields.visitPlace")}</Form.Label>
              {readOnly ? (
                <div className="form-control-plaintext">{getVisitPlaceLabel(f.visit_place, t) || "—"}</div>
              ) : (
                <Form.Select
                  name="visit_place"
                  value={f.visit_place || "на приеме"}
                  onChange={handleChange}>
                  {VISIT_PLACES.map((p) => (
                    <option key={p.value} value={p.value}>
                      {t(p.labelKey)}
                    </option>
                  ))}
                </Form.Select>
              )}
            </div>
            <div className="ppv-field ppv-field--grow">
              <Form.Label>{t("ppv.fields.doctor")}</Form.Label>
              {readOnly ? (
                <div className="form-control-plaintext">{f.doctor || "—"}</div>
              ) : (
                renderDoctorSelect()
              )}
            </div>
          </div>
        </div>

        <div className="ppv-section ppv-section--lined ppv-section--multiline">
          <div className="ppv-section-title">{t("docs.labels.complaints")}</div>
          {readOnly ? (
            <div className="ppv-section-value">{f.complaints || "—"}</div>
          ) : (
            <Form.Control
              as="textarea"
              className="ppv-section-value"
              name="complaints"
              rows={2}
              value={f.complaints || ""}
              onChange={handleChange}
            />
          )}
        </div>

        <div className="ppv-section ppv-section--lined ppv-section--multiline">
          <div className="ppv-section-title">{t("ppv.fields.anamnesis")}</div>
          {readOnly ? (
            <div className="ppv-section-value">{f.anamnesis || "—"}</div>
          ) : (
            <Form.Control
              as="textarea"
              className="ppv-section-value"
              name="anamnesis"
              rows={3}
              value={f.anamnesis || ""}
              onChange={handleChange}
            />
          )}
        </div>

        <div className="ppv-section ppv-section--lined ppv-section--multiline">
          <div className="ppv-section-title">{t("ppv.fields.objectiveData")}</div>
          {readOnly ? (
            <div className="ppv-section-value">{f.objective_data || "—"}</div>
          ) : (
            <Form.Control
              as="textarea"
              className="ppv-section-value"
              name="objective_data"
              rows={3}
              value={f.objective_data || ""}
              onChange={handleChange}
            />
          )}
        </div>

        <div className="ppv-section ppv-section--lined ppv-section--diag">
          <div className="ppv-section-title">{t("ppv.fields.mainDiagnosis")}</div>
          {readOnly ? (
            <div className="ppv-section-value">{f.main_diagnosis || "—"}</div>
          ) : (
            <Form.Control
              type="text"
              className="ppv-section-value"
              name="main_diagnosis"
              value={f.main_diagnosis || ""}
              onChange={handleChange}
            />
          )}
          <div className="ppv-section-title ppv-section-title--mkb">{t("ppv.fields.mkb")}</div>
          {readOnly ? (
            <div className="ppv-section-value ppv-section-value--mkb">
              {f.main_diagnosis_mkb || "—"}
            </div>
          ) : (
            <Form.Control
              type="text"
              className="ppv-section-value ppv-section-value--mkb"
              name="main_diagnosis_mkb"
              value={f.main_diagnosis_mkb || ""}
              onChange={handleChange}
            />
          )}
        </div>

        <div className="ppv-section ppv-section--lined ppv-section--diag">
          <div className="ppv-section-title">{t("ppv.fields.complications")}</div>
          {readOnly ? (
            <div className="ppv-section-value">{f.complications || "—"}</div>
          ) : (
            <Form.Control
              type="text"
              className="ppv-section-value"
              name="complications"
              value={f.complications || ""}
              onChange={handleChange}
            />
          )}
          <div className="ppv-section-title ppv-section-title--mkb">{t("ppv.fields.mkb")}</div>
          {readOnly ? (
            <div className="ppv-section-value ppv-section-value--mkb">
              {f.complications_mkb || "—"}
            </div>
          ) : (
            <Form.Control
              type="text"
              className="ppv-section-value ppv-section-value--mkb"
              name="complications_mkb"
              value={f.complications_mkb || ""}
              onChange={handleChange}
            />
          )}
        </div>

        <div className="ppv-section ppv-section--lined ppv-section--diag">
          <div className="ppv-section-title">{t("ppv.fields.concomitant")}</div>
          {readOnly ? (
            <div className="ppv-section-value">{f.concomitant_1 || "—"}</div>
          ) : (
            <Form.Control
              type="text"
              className="ppv-section-value"
              name="concomitant_1"
              value={f.concomitant_1 || ""}
              onChange={handleChange}
            />
          )}
          <div className="ppv-section-title ppv-section-title--mkb">{t("ppv.fields.mkb")}</div>
          {readOnly ? (
            <div className="ppv-section-value ppv-section-value--mkb">
              {f.concomitant_1_mkb || "—"}
            </div>
          ) : (
            <Form.Control
              type="text"
              className="ppv-section-value ppv-section-value--mkb"
              name="concomitant_1_mkb"
              value={f.concomitant_1_mkb || ""}
              onChange={handleChange}
            />
          )}
        </div>

        <div className="ppv-section ppv-section--lined ppv-section--diag">
          <div className="ppv-section-title">{t("ppv.fields.concomitantExtra")}</div>
          {readOnly ? (
            <div className="ppv-section-value">{f.concomitant_2 || "—"}</div>
          ) : (
            <Form.Control
              type="text"
              className="ppv-section-value"
              name="concomitant_2"
              value={f.concomitant_2 || ""}
              onChange={handleChange}
            />
          )}
          <div className="ppv-section-title ppv-section-title--mkb">{t("ppv.fields.mkb")}</div>
          {readOnly ? (
            <div className="ppv-section-value ppv-section-value--mkb">
              {f.concomitant_2_mkb || "—"}
            </div>
          ) : (
            <Form.Control
              type="text"
              className="ppv-section-value ppv-section-value--mkb"
              name="concomitant_2_mkb"
              value={f.concomitant_2_mkb || ""}
              onChange={handleChange}
            />
          )}
        </div>

        <div className="ppv-section ppv-section--lined ppv-section--diag ppv-section--title-wrap">
          <div className="ppv-section-title">
            {t("ppv.fields.externalCause")}
          </div>
          {readOnly ? (
            <div className="ppv-section-value">{f.external_cause || "—"}</div>
          ) : (
            <Form.Control
              type="text"
              className="ppv-section-value"
              name="external_cause"
              value={f.external_cause || ""}
              onChange={handleChange}
            />
          )}
          <div className="ppv-section-title ppv-section-title--mkb">{t("ppv.fields.mkb")}</div>
          {readOnly ? (
            <div className="ppv-section-value ppv-section-value--mkb">
              {f.external_cause_mkb || "—"}
            </div>
          ) : (
            <Form.Control
              type="text"
              className="ppv-section-value ppv-section-value--mkb"
              name="external_cause_mkb"
              value={f.external_cause_mkb || ""}
              onChange={handleChange}
            />
          )}
        </div>

        <div className="ppv-section ppv-section--lined ppv-section--split">
          <div className="ppv-section-title">{t("ppv.fields.healthGroup")}</div>
          {readOnly ? (
            <div className="ppv-section-value">{f.health_group || "—"}</div>
          ) : (
            <Form.Control
              type="text"
              className="ppv-section-value"
              name="health_group"
              value={f.health_group || ""}
              onChange={handleChange}
            />
          )}
          <div className="ppv-section-title">{t("ppv.fields.dispensaryObservation")}</div>
          {readOnly ? (
            <div className="ppv-section-value">
              {f.dispensary_observation || "—"}
            </div>
          ) : (
            <Form.Control
              type="text"
              className="ppv-section-value"
              name="dispensary_observation"
              value={f.dispensary_observation || ""}
              onChange={handleChange}
            />
          )}
        </div>

        <div className="ppv-section ppv-section--lined ppv-section--appointment">
          <div className="ppv-section-title">
            {t("docs.labels.prescriptions")}
          </div>
          {readOnly ? (
            <div className="ppv-section-value">{f.prescriptions || "—"}</div>
          ) : (
            <Form.Control
              as="textarea"
              className="ppv-section-value"
              name="prescriptions"
              rows={4}
              value={f.prescriptions || ""}
              onChange={handleChange}
            />
          )}
        </div>

        <div className="ppv-section ppv-section--lined ppv-section--appointment">
          <div className="ppv-section-title">
            {t("docs.labels.medications")}
          </div>
          {readOnly ? (
            <div className="ppv-section-value">{f.medications || "—"}</div>
          ) : (
            <Form.Control
              as="textarea"
              className="ppv-section-value"
              name="medications"
              rows={4}
              value={f.medications || ""}
              onChange={handleChange}
            />
          )}
        </div>

        <div className="ppv-section ppv-section--lined ppv-section--appointment">
          <div className="ppv-section-title">
            {t("docs.labels.sickLeave")}
          </div>
          {readOnly ? (
            <div className="ppv-section-value">{f.sick_leave || "—"}</div>
          ) : (
            <Form.Control
              as="textarea"
              className="ppv-section-value"
              name="sick_leave"
              rows={3}
              value={f.sick_leave || ""}
              onChange={handleChange}
            />
          )}
        </div>

        <div className="ppv-section ppv-section--lined ppv-section--appointment">
          <div className="ppv-section-title">
            {t("docs.labels.preferentialPrescriptions")}
          </div>
          {readOnly ? (
            <div className="ppv-section-value">
              {f.preferential_prescriptions || "—"}
            </div>
          ) : (
            <Form.Control
              as="textarea"
              className="ppv-section-value"
              name="preferential_prescriptions"
              rows={3}
              value={f.preferential_prescriptions || ""}
              onChange={handleChange}
            />
          )}
        </div>

        <div className="ppv-appointments-consent">
          {t("ppv.consent")}
        </div>

        <div className="ppv-appointments-sign">
          <span className="ppv-appointments-sign-label">
            {t("ppv.signLabel")}
          </span>
          {readOnly ? (
            <span className="ppv-appointments-sign-value">
              {f.doctor || "—"}
            </span>
          ) : (
            renderDoctorSelect("ppv-appointments-sign-input")
          )}
          <span className="ppv-appointments-sign-slash">/</span>
          <span className="ppv-appointments-sign-mark" />
          <span className="ppv-appointments-sign-caption">{t("ppv.signature")}</span>
        </div>
      </Form>

      {/* Печатная форма */}
      <div className="ppv-print">
        <div className="ppv-print-meta">
          <div className="ppv-print-meta-line">
            <span>{t("ppv.fields.examDate")}</span>
            <span className="ppv-print-underline ppv-print-underline--date">
              {formatDateForDisplay(savedFormData.exam_date)}
            </span>
            <span className="ppv-print-place">
              {VISIT_PLACES.map((p, i) => (
                <React.Fragment key={p.value}>
                  {i > 0 ? ", " : ""}
                  <span
                    className={
                      savedFormData.visit_place === p.value ? "is-marked" : ""
                    }>
                    {t(p.labelKey)}
                  </span>
                </React.Fragment>
              ))}
              .
            </span>
          </div>
          <div className="ppv-print-meta-line">
            <span>{t("ppv.fields.doctor")}</span>
            <span className="ppv-print-underline ppv-print-underline--doctor">
              {savedFormData.doctor}
            </span>
          </div>
        </div>

        <div className="ppv-print-block">
          <div className="ppv-print-block-label">{t("docs.labels.complaints")}:</div>
          <div className="ppv-print-lined ppv-print-lined--3">
            {savedFormData.complaints}
          </div>
        </div>

        <div className="ppv-print-block">
          <div className="ppv-print-block-label">{t("ppv.fields.anamnesis")}:</div>
          <div className="ppv-print-lined ppv-print-lined--10">
            {savedFormData.anamnesis}
          </div>
        </div>

        <div className="ppv-print-block">
          <div className="ppv-print-block-label">{t("ppv.fields.objectiveData")}</div>
          <div className="ppv-print-lined ppv-print-lined--12">
            {savedFormData.objective_data}
          </div>
        </div>

        <div className="ppv-print-diag">
          <div className="ppv-print-diag-row">
            <span className="ppv-print-diag-label">
              {t("ppv.fields.mainDiagnosis")}:
            </span>
            <span className="ppv-print-diag-text">
              {savedFormData.main_diagnosis}
            </span>
            <span className="ppv-print-diag-mkb-label">{t("ppv.fields.mkb")}</span>
            <span className="ppv-print-diag-mkb">
              {savedFormData.main_diagnosis_mkb}
            </span>
          </div>
          <div className="ppv-print-diag-row">
            <span className="ppv-print-diag-label">{t("ppv.fields.complications")}:</span>
            <span className="ppv-print-diag-text">
              {savedFormData.complications}
            </span>
            <span className="ppv-print-diag-mkb-label">{t("ppv.fields.mkb")}</span>
            <span className="ppv-print-diag-mkb">
              {savedFormData.complications_mkb}
            </span>
          </div>
          <div className="ppv-print-diag-row">
            <span className="ppv-print-diag-label">{t("ppv.fields.concomitant")}</span>
            <span className="ppv-print-diag-text">
              {savedFormData.concomitant_1}
            </span>
            <span className="ppv-print-diag-mkb-label">{t("ppv.fields.mkb")}</span>
            <span className="ppv-print-diag-mkb">
              {savedFormData.concomitant_1_mkb}
            </span>
          </div>
          <div className="ppv-print-diag-row">
            <span className="ppv-print-diag-label" style={{ visibility: "hidden" }}>
              {t("ppv.fields.concomitant")}
            </span>
            <span className="ppv-print-diag-text">
              {savedFormData.concomitant_2}
            </span>
            <span className="ppv-print-diag-mkb-label">{t("ppv.fields.mkb")}</span>
            <span className="ppv-print-diag-mkb">
              {savedFormData.concomitant_2_mkb}
            </span>
          </div>
          <div className="ppv-print-diag-row">
            <span className="ppv-print-diag-label">
              {t("ppv.fields.externalCause")}
            </span>
            <span className="ppv-print-diag-text">
              {savedFormData.external_cause}
            </span>
            <span className="ppv-print-diag-mkb-label">{t("ppv.fields.mkb")}</span>
            <span className="ppv-print-diag-mkb">
              {savedFormData.external_cause_mkb}
            </span>
          </div>
        </div>

        <div className="ppv-print-health">
          <div className="ppv-print-health-item">
            <span>{t("ppv.fields.healthGroup")}</span>
            <span className="ppv-print-underline" style={{ minWidth: 80 }}>
              {savedFormData.health_group}
            </span>
          </div>
          <div className="ppv-print-health-item">
            <span>{t("ppv.fields.dispensaryObservation")}</span>
            <span className="ppv-print-underline" style={{ flex: 1 }}>
              {savedFormData.dispensary_observation}
            </span>
          </div>
        </div>

        <table className="ppv-print-table">
          <thead>
            <tr>
              <th>{t("docs.labels.prescriptions")}</th>
              <th>{t("docs.labels.medications")}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="ppv-print-cell ppv-print-cell--tall">
                {savedFormData.prescriptions}
              </td>
              <td className="ppv-print-cell ppv-print-cell--tall">
                {savedFormData.medications}
              </td>
            </tr>
            <tr>
              <td>
                <strong>{t("docs.labels.sickLeave")}</strong>
                <div className="ppv-print-cell">{savedFormData.sick_leave}</div>
              </td>
              <td>
                <strong>{t("docs.labels.preferentialPrescriptions")}</strong>
                <div className="ppv-print-cell">
                  {savedFormData.preferential_prescriptions}
                </div>
              </td>
            </tr>
            <tr>
              <td colSpan={2} className="ppv-print-consent-cell">
                {t("ppv.consent")}
              </td>
            </tr>
            <tr>
              <td colSpan={2}>
                <div className="ppv-print-sign">
                  <span>
                    {t("ppv.signLabel")}
                  </span>
                  <span className="ppv-print-sign-line">
                    {savedFormData.doctor}
                  </span>
                  <span className="ppv-print-sign-slash">/</span>
                  <span className="ppv-print-sign-mark" />
                  <span className="ppv-print-sign-caption">{t("ppv.signature")}</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Body>{modalMessage}</Modal.Body>
        <Modal.Footer>
          <Button
            icon="check"
            onClick={() => setShowModal(false)}
            variant="primary">
            {t("actions.ok", { ns: "common" })}
          </Button>
        </Modal.Footer>
      </Modal>

      <AiVoiceDictationModal
        show={showVoiceModal}
        onHide={() => setShowVoiceModal(false)}
        patientId={patientId}
        templateId={1}
        formData={formData}
        onApply={handleVoiceApply}
        onUndo={handleVoiceUndo}
      />
    </>
  );
}

export default PatientPrimaryVisit;
